<script setup>
import { computed, onMounted, reactive } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useVuelidate } from '@vuelidate/core';
import { required, minLength } from '@vuelidate/validators';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAccount } from 'dashboard/composables/useAccount';
import { useAlert } from 'dashboard/composables';
import SettingsLayout from '../../settings/SettingsLayout.vue';
import BaseSettingsHeader from '../../settings/components/BaseSettingsHeader.vue';
import WithLabel from 'v3/components/Form/WithLabel.vue';
import NextInput from 'next/input/Input.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const { t } = useI18n();
const store = useStore();
const route = useRoute();
const router = useRouter();
const { accountScopedRoute } = useAccount();

const templateId = computed(() =>
  route.params.templateId ? Number(route.params.templateId) : null
);
const isEditMode = computed(() => !!templateId.value);

const templates = useMapGetter('accountTemplates/getAccountTemplates');
const featureFlags = useMapGetter('accountTemplates/getFeatureFlagsList');
const uiFlags = useMapGetter('accountTemplates/getUIFlags');

const form = reactive({
  name: '',
  description: '',
  evolutionApiUrl: '',
  evolutionApiKey: '',
  limitsAgents: '',
  limitsInboxes: '',
  disabledFeatureNames: [],
  enabledFeatureNames: [],
  defaultLabels: [],
  kanbanBoards: [],
});

const rules = {
  name: { required, minLength: minLength(2) },
};
const v$ = useVuelidate(rules, form);

const populateForm = template => {
  if (!template) return;
  form.name = template.name || '';
  form.description = template.description || '';
  form.evolutionApiUrl = template.evolution_api_url || '';
  form.evolutionApiKey = template.evolution_api_key || '';
  form.limitsAgents = template.limits?.agents ?? '';
  form.limitsInboxes = template.limits?.inboxes ?? '';
  form.disabledFeatureNames = [...(template.disabled_feature_names || [])];
  form.enabledFeatureNames = [...(template.enabled_feature_names || [])];
  form.defaultLabels = (template.default_labels || []).map(l => ({ ...l }));
  form.kanbanBoards = (template.kanban_boards || []).map(b => ({
    name: b.name,
    stages: (b.stages || []).map(s => ({ ...s })),
  }));
};

onMounted(async () => {
  store.dispatch('accountTemplates/fetchFeatureFlagsList');
  if (isEditMode.value) {
    await store.dispatch('accountTemplates/fetchAccountTemplates');
    populateForm(templates.value.find(tpl => tpl.id === templateId.value));
  }
});

const addLabel = () => {
  form.defaultLabels.push({ title: '', color: '#1f93ff', description: '' });
};
const removeLabel = index => form.defaultLabels.splice(index, 1);

const addKanbanBoard = () => {
  form.kanbanBoards.push({ name: '', stages: [] });
};
const removeKanbanBoard = index => form.kanbanBoards.splice(index, 1);

const addStage = board => {
  board.stages.push({ name: '', color: '#1f93ff' });
};
const removeStage = (board, index) => board.stages.splice(index, 1);

const submit = async () => {
  v$.value.$touch();
  if (v$.value.$invalid) return;

  const payload = {
    name: form.name,
    description: form.description,
    evolution_api_url: form.evolutionApiUrl,
    evolution_api_key: form.evolutionApiKey,
    limits: {
      ...(form.limitsAgents !== ''
        ? { agents: Number(form.limitsAgents) }
        : {}),
      ...(form.limitsInboxes !== ''
        ? { inboxes: Number(form.limitsInboxes) }
        : {}),
    },
    disabled_feature_names: form.disabledFeatureNames,
    enabled_feature_names: form.enabledFeatureNames,
    default_labels: form.defaultLabels.filter(l => l.title),
    kanban_boards: form.kanbanBoards
      .filter(b => b.name)
      .map(b => ({ ...b, stages: b.stages.filter(s => s.name) })),
  };

  try {
    if (isEditMode.value) {
      await store.dispatch('accountTemplates/updateAccountTemplate', {
        id: templateId.value,
        ...payload,
      });
      useAlert(t('SUPER_ADMIN.TEMPLATES.FORM.UPDATE_SUCCESS'));
    } else {
      await store.dispatch('accountTemplates/createAccountTemplate', payload);
      useAlert(t('SUPER_ADMIN.TEMPLATES.FORM.CREATE_SUCCESS'));
    }
    router.push(accountScopedRoute('super_admin_templates_list'));
  } catch (error) {
    useAlert(error?.message || t('SUPER_ADMIN.TEMPLATES.FORM.ERROR'));
  }
};
</script>

<template>
  <SettingsLayout>
    <template #header>
      <BaseSettingsHeader
        :title="
          isEditMode
            ? t('SUPER_ADMIN.TEMPLATES.FORM.EDIT_TITLE')
            : t('SUPER_ADMIN.TEMPLATES.FORM.NEW_TITLE')
        "
        :description="t('SUPER_ADMIN.TEMPLATES.FORM.DESCRIPTION')"
      />
    </template>

    <template #body>
      <form class="grid gap-6 max-w-2xl" @submit.prevent="submit">
        <section class="grid gap-4">
          <WithLabel :label="t('SUPER_ADMIN.TEMPLATES.FORM.NAME.LABEL')">
            <NextInput v-model="form.name" type="text" class="w-full" />
          </WithLabel>
          <WithLabel
            :label="t('SUPER_ADMIN.TEMPLATES.FORM.DESCRIPTION_FIELD.LABEL')"
          >
            <NextInput v-model="form.description" type="text" class="w-full" />
          </WithLabel>
        </section>

        <section class="grid gap-4">
          <h4 class="text-heading-3 text-n-slate-12">
            {{ t('SUPER_ADMIN.TEMPLATES.FORM.EVOLUTION.TITLE') }}
          </h4>
          <WithLabel
            :label="t('SUPER_ADMIN.TEMPLATES.FORM.EVOLUTION.URL_LABEL')"
          >
            <NextInput
              v-model="form.evolutionApiUrl"
              type="text"
              class="w-full"
              placeholder="https://..."
            />
          </WithLabel>
          <WithLabel
            :label="t('SUPER_ADMIN.TEMPLATES.FORM.EVOLUTION.KEY_LABEL')"
          >
            <NextInput
              v-model="form.evolutionApiKey"
              type="password"
              class="w-full"
            />
          </WithLabel>
        </section>

        <section class="grid gap-4">
          <h4 class="text-heading-3 text-n-slate-12">
            {{ t('SUPER_ADMIN.TEMPLATES.FORM.LIMITS.TITLE') }}
          </h4>
          <div class="grid grid-cols-2 gap-4">
            <WithLabel :label="t('SUPER_ADMIN.TEMPLATES.FORM.LIMITS.AGENTS')">
              <NextInput
                v-model="form.limitsAgents"
                type="number"
                class="w-full"
              />
            </WithLabel>
            <WithLabel :label="t('SUPER_ADMIN.TEMPLATES.FORM.LIMITS.INBOXES')">
              <NextInput
                v-model="form.limitsInboxes"
                type="number"
                class="w-full"
              />
            </WithLabel>
          </div>
        </section>

        <section class="grid gap-2">
          <h4 class="text-heading-3 text-n-slate-12">
            {{ t('SUPER_ADMIN.TEMPLATES.FORM.FEATURES.TITLE') }}
          </h4>
          <p class="text-body-main text-n-slate-11">
            {{ t('SUPER_ADMIN.TEMPLATES.FORM.FEATURES.NOTE') }}
          </p>
          <div class="grid grid-cols-2 gap-x-4 gap-y-2 mt-2">
            <label
              v-for="flag in featureFlags"
              :key="flag.name"
              class="flex items-center gap-2 text-body-main text-n-slate-12"
            >
              <input
                v-model="form.disabledFeatureNames"
                type="checkbox"
                :value="flag.name"
              />
              {{ flag.display_name }}
            </label>
          </div>
        </section>

        <section class="grid gap-2">
          <h4 class="text-heading-3 text-n-slate-12">
            {{ t('SUPER_ADMIN.TEMPLATES.FORM.FEATURES_ENABLE.TITLE') }}
          </h4>
          <p class="text-body-main text-n-slate-11">
            {{ t('SUPER_ADMIN.TEMPLATES.FORM.FEATURES_ENABLE.NOTE') }}
          </p>
          <div class="grid grid-cols-2 gap-x-4 gap-y-2 mt-2">
            <label
              v-for="flag in featureFlags"
              :key="flag.name"
              class="flex items-center gap-2 text-body-main text-n-slate-12"
            >
              <input
                v-model="form.enabledFeatureNames"
                type="checkbox"
                :value="flag.name"
              />
              {{ flag.display_name }}
            </label>
          </div>
        </section>

        <section class="grid gap-2">
          <div class="flex items-center justify-between">
            <h4 class="text-heading-3 text-n-slate-12">
              {{ t('SUPER_ADMIN.TEMPLATES.FORM.LABELS.TITLE') }}
            </h4>
            <Button
              type="button"
              faded
              slate
              sm
              :label="t('SUPER_ADMIN.TEMPLATES.FORM.LABELS.ADD')"
              @click="addLabel"
            />
          </div>
          <div
            v-for="(label, index) in form.defaultLabels"
            :key="index"
            class="flex items-center gap-2"
          >
            <NextInput
              v-model="label.title"
              type="text"
              class="flex-1"
              :placeholder="
                t('SUPER_ADMIN.TEMPLATES.FORM.LABELS.NAME_PLACEHOLDER')
              "
            />
            <input
              v-model="label.color"
              type="color"
              class="w-10 h-10 shrink-0"
            />
            <Button
              type="button"
              icon="i-woot-bin"
              slate
              sm
              @click="removeLabel(index)"
            />
          </div>
        </section>

        <section class="grid gap-3">
          <div class="flex items-center justify-between">
            <h4 class="text-heading-3 text-n-slate-12">
              {{ t('SUPER_ADMIN.TEMPLATES.FORM.KANBANS.TITLE') }}
            </h4>
            <Button
              type="button"
              faded
              slate
              sm
              :label="t('SUPER_ADMIN.TEMPLATES.FORM.KANBANS.ADD_BOARD')"
              @click="addKanbanBoard"
            />
          </div>

          <div
            v-for="(board, boardIndex) in form.kanbanBoards"
            :key="boardIndex"
            class="border border-n-weak rounded-lg p-4 grid gap-3"
          >
            <div class="flex items-center gap-2">
              <NextInput
                v-model="board.name"
                type="text"
                class="flex-1"
                :placeholder="
                  t('SUPER_ADMIN.TEMPLATES.FORM.KANBANS.BOARD_NAME_PLACEHOLDER')
                "
              />
              <Button
                type="button"
                icon="i-woot-bin"
                slate
                sm
                @click="removeKanbanBoard(boardIndex)"
              />
            </div>

            <div
              v-for="(stage, stageIndex) in board.stages"
              :key="stageIndex"
              class="flex items-center gap-2 pl-4"
            >
              <NextInput
                v-model="stage.name"
                type="text"
                class="flex-1"
                :placeholder="
                  t('SUPER_ADMIN.TEMPLATES.FORM.KANBANS.STAGE_NAME_PLACEHOLDER')
                "
              />
              <input
                v-model="stage.color"
                type="color"
                class="w-10 h-10 shrink-0"
              />
              <Button
                type="button"
                icon="i-woot-bin"
                slate
                sm
                @click="removeStage(board, stageIndex)"
              />
            </div>

            <Button
              type="button"
              faded
              slate
              sm
              class="justify-self-start"
              :label="t('SUPER_ADMIN.TEMPLATES.FORM.KANBANS.ADD_STAGE')"
              @click="addStage(board)"
            />
          </div>
        </section>

        <div class="flex justify-end gap-2">
          <router-link :to="accountScopedRoute('super_admin_templates_list')">
            <Button
              type="button"
              faded
              slate
              :label="t('SUPER_ADMIN.CANCEL')"
            />
          </router-link>
          <Button
            type="submit"
            :is-loading="uiFlags.creatingItem || uiFlags.updatingItem"
            :label="t('SUPER_ADMIN.TEMPLATES.FORM.SUBMIT')"
          />
        </div>
      </form>
    </template>
  </SettingsLayout>
</template>
